/** @jsxImportSource @emotion/react */
import useConfig from "../../../../actions/Tenants/config/configHook";
import MetaForm from "../../../Forms/MetaForm";
import { ReportTemplate } from "../../Layers/Layer/Report/utils";
import CategoryConfigForm from "./CategoryConfigForm";
import DefectGroupConfigForm from "./DefectGroupConfigForm";
import { DisplayFilterForm } from "./DisplayFilterForm";
import FieldConfigForm from "./FieldConfigForm";
import { PDFDynamicSectionsConfigForm } from "./PDFDynamicSectionsConfigForm";
import SanitizeUnexpectedFields from "./SanitizeUnexpectedFields";
import { yesOrNoRadio } from "./util";

export const defaultPdfReport = {
    text: "Intake",
    value: "intake",
    layer_type: "purchase_order",
    internal_defects_title: "",
    external_defects_title: "",
    children_table_fields: [],
    children_check_summary_meta_fields: [],
    check_meta_fields: [],
    fallback_check_types: [],
    children_level: 2,
    audience: [] // empty => both supplier & end_client
};

export const intakeReportViewTypes = [
    { label: "Full report", value: "full", default: true },
    { label: "Summary", value: "summary" },
    { label: "Only red pallets", value: "danger" },
    { label: "Only amber pallets", value: "warning" },
    { label: "Amber and red pallets", value: "warning,danger" },
];

const ReportTemplateOptions = [
    { label: "Intake ( PO with per page a pallet )", value: ReportTemplate.intake },
    { label: "Storage  (PO with per page a check summary of 1 day per pallet)", value: ReportTemplate.storage },
    { label: "Advance ripening (PO with per page a pallet with table with a row per day)", value: ReportTemplate.advance_ripening },
    { label: "General check summary for check type (latest check with fruit table)", value: ReportTemplate.latest_check },
    { label: "Dynamic sections", value: ReportTemplate.dynamic_sections },
];

export default function PDFReportConfigForm({ config_data, setConfigData }) {
    const config = useConfig();

    const form = [
        {
            type: "header",
            label: "General configuration"
        },
        {
            label: "Value",
            description: `What is the value/id of this pdf report. If not ART, it will be used to fetch checks and check config of this type. This value will also be used in the url, blob_name and in qc_status field in layer meta.`,
            name: "value",
            type: "text",
            onchange_callback: ({ field, value }) => {
                setConfigData({ ...config_data, [field]: value.toLowerCase().replace(/[^a-z0-9]/g, "_") });
            },
        },
        {
            label: "Label",
            description: `What is the label of this pdf report. Used in the button (Print ${config_data.text}).`,
            name: "text",
            type: "text"
        },
        {
            label: "Title",
            description: `What is the title of this pdf report. Used in the header of the report and as email subject. Include "Report" in the title if needed. Will display as "${config_data.title} for {label}".`,
            name: "title",
            type: "text"
        },
        {
            label: "Layer type",
            description: "On which layer type do you want to generate this report?",
            name: "layer_type",
            type: "single-select",
            options: config.layer_types.map((i) => ({ value: i.value, label: i.text }))
        },
        {
            label: "Template",
            description: "What template to use for this report?",
            name: "template",
            type: "single-select",
            options: ReportTemplateOptions.map(({ label, value }) => ({ label, value }))
        },
        {
            label: "Report hierarchy depth",
            description: "Choose whether dynamic reports include only direct children or also grandchildren. Existing reports default to children and grandchildren.",
            name: "children_level",
            type: "single-select",
            options: [
                { label: "Direct children only", value: 1 },
                { label: "Children and grandchildren", value: 2 },
            ],
        },
        {
            type: "header",
            label: "Report content"
        },
        {
            type: "element",
            label: "Dynamic sections config",
            description: "Dynamic sections configuration.",
            name: "dynamic_sections",
            el: <PDFDynamicSectionsConfigForm
                fields={config_data.dynamic_sections || []}
                description="Dynamic sections configuration."
                name="dynamic_sections"
                title="Dynamic sections config"
                setArray={(dynamic_sections) => setConfigData({ ...config_data, dynamic_sections })}
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
            />
        },
        {
            type: "header",
            label: "Report detail page options and actions"
        },
        {
            label: "Teams that can send the report",
            name: "teams_that_can_send",
            description: "Teams that can send the report. Leave empty to hide button. If the button is not showing, add the appropriate team here.",
            type: "select-managed",
            multi: true,
            return_array: true,
            options_name: "teams",
        },
        {
            label: "Teams that can manage QC status of the report",
            name: "teams_that_can_manage_qc_status",
            description: "Teams that can manage QC status of the report (Mark ready for review). Leave empty to hide button. If the button is not showing, add the appropriate team here.",
            type: "select-managed",
            multi: true,
            return_array: true,
            options_name: "teams",
        },
        {
            label: "Save draft report",
            name: "text_save_draft_report",
            description: "Text to show on the button to save the draft report. By default is 'Save draft report'.",
            type: "text",
        },
        {
            label: "Edit report",
            name: "text_edit_report",
            description: "Text to show on the button to edit report. By default is 'Edit'.",
            type: "text",
        },
        {
            label: "Report sent",
            name: "text_report_sent",
            description: "Text to show on the button to send report. By default is 'Report sent'.",
            type: "text",
        },
        {
            label: "Check types",
            description: "What check types to include in the report. Use to overwrite default behaviour from setting above.",
            name: "check_types",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }))
        },
        {
            label: "Fallback check types",
            description: "Fallback check types to use when no check is found for the selected check types.",
            name: "fallback_check_types",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }))
        },
        {
            label: "Fallback check max age (days)",
            description: "Maximum age (in days) of fallback checks to include per fruit type. Checks older than this will not be inherited. E.g. 3 for avocado, 2 for mango.",
            name: "fallback_check_max_age_days",
            type: "object-list",
            getItemTitle: (item) => `${item.fruit_type || "—"}: ${item.max_age_days ?? "—"} days`,
            fields: [
                {
                    label: "Fruit type",
                    name: "fruit_type",
                    type: "single-select",
                    options: config.fruit_types.map(({ value, text }) => ({ value, label: text || value })),
                },
                {
                    label: "Max age (days)",
                    name: "max_age_days",
                    type: "number",
                },
            ],
        },
        {
            label: "Show Dry matter?",
            name: "show_dry_matter",
            description: "Show dry matter in the pdf report.",
            ...yesOrNoRadio
        },
        {
            label: "Display filter",
            type: "element",
            name: "display_filter",
            el: <DisplayFilterForm
                object={config_data.display_filter}
                setValue={(field, value) => setConfigData({ ...config_data, display_filter: { ...config_data.display_filter, [field]: value } })}
                attributes={["fruit_types", "exclude_fruit_types", "facilities", "exclude_facilities", "meta_conditions"]} />
        },
        {
            type: "element",
            name: "children_table_fields",
            label: "Children table fields",
            description: "What fields to display in the children (pallet) table. Only for purchase order layer type.",
            el: <FieldConfigForm
                fields={config_data.children_table_fields || []}
                description="What fields to display in the children (pallet) table. Only for purchase order layer type."
                name="children_table_fields"
                title="Children table fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                setArray={(children_table_fields) => setConfigData({ ...config_data, children_table_fields })} />
        },
        {
            type: "element",
            name: "pallet_meta_fields",
            label: "Pallet meta fields",
            description: "What contextual data to display for pallet in ART report.",
            el: <FieldConfigForm
                fields={config_data.pallet_meta_fields || []}
                description="What contextual data to display for pallet in ART report."
                name="pallet_meta_fields"
                title="Pallet meta fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                setArray={(pallet_meta_fields) => setConfigData({ ...config_data, pallet_meta_fields })} />
        },
        {
            type: "element",
            name: "children_check_summary_meta_fields",
            label: "Check summary fields",
            description: "What fields to show on the check summary.",
            el: <FieldConfigForm
                fields={config_data.children_check_summary_meta_fields || []}
                description="What fields to show on the check summary."
                name="children_check_summary_meta_fields"
                title="Check summary fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                setArray={(children_check_summary_meta_fields) => setConfigData({ ...config_data, children_check_summary_meta_fields })} />
        },
        {
            type: "element",
            name: "check_meta_fields",
            label: "Check meta fields",
            description: "Check meta fields to display Intake report. Like Unapproved grower, Has contaminated fruit.",
            el: <FieldConfigForm
                fields={config_data.check_meta_fields || []}
                description="Check meta fields to display Intake report. Like Unapproved grower, Has contaminated fruit."
                name="check_meta_fields"
                title="Check meta fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                setArray={(check_meta_fields) => setConfigData({ ...config_data, check_meta_fields })} />
        },
        {
            type: "element",
            name: "defect_groups",
            label: "Defect groups",
            description: "What group of defects do you want to show at the header of the check summary page?",
            el: <DefectGroupConfigForm
                fields={config_data.defect_groups || []}
                description="What group of defects do you want to show at the header of the check summary page?"
                name="defect_groups"
                title="Defect groups"
                setArray={(defect_groups) => setConfigData({ ...config_data, defect_groups })} />
        },
        {
            type: "element",
            label: "Categories",
            description: "Categories for overall decision making.",
            el: <CategoryConfigForm
                fields={config_data.categories || []}
                description="Categories for overall decision making."
                name="categories"
                useDisplayFilter={false}
                title="Categories"
                setArray={(categories) => setConfigData({ ...config_data, categories })} />
        },
        // TODO: create dropdown for flag fields
        {
            label: "Manual flag field",
            name: "manual_flag_field",
            description: "Field to manually flag the report. Leave empty to not use manual flag.",
            type: "text",
        },
        {
            label: "Business rule flag field",
            name: "business_rule_flag_field",
            description: "Field to automatically flag the report based on business rules. Leave empty to not use business rule flag.",
            type: "text",
        },
        {
            type: "header",
            label: "Emails"
        },
        {
            label: "Report audience",
            name: "audience",
            description: "Who should receive the report emails. Select one or both. Leave empty to send internally only.",
            type: "single-select",
            multi: true,
            return_array: true,
            options: [
                { label: "Supplier", value: "supplier" },
                { label: "End client", value: "end_client" }
            ]
        },
        {
            label: "Use parent label in email subject",
            description: "Use the parent label in the email subject. Most likely needed for Production and Reassessment reports.",
            name: "parent_label_in_subject",
            ...yesOrNoRadio
        },
        {
            label: "Use custom template for email body",
            description: "Use custom template for the email body.",
            name: "custom_email_body",
            ...yesOrNoRadio
        },
        {
            label: "Custom email greeting",
            name: "email_greeting",
            type: "text",
        },
        {
            label: "Custom email body",
            description: "Custom email body. The report link will be included after this paragraph.",
            name: "email_body",
            type: "textarea",
            textInputProps: { rows: 3 }
        },
        {
            label: "Custom email signature",
            name: "email_signature",
            type: "textarea",
            textInputProps: { rows: 5 }
        },
        {
            type: "header",
            label: "Intake report options"
        },
        {
            label: "Intake report view types",
            name: "view_type",
            description: "View types to show on the intake report. Leaving it empty will disable the view type selector and show all pages of the report.",
            type: "single-select",
            multi: true,
            return_array: true,
            options: intakeReportViewTypes,
        },
        {
            type: "header",
            label: "Deprecated options",
            name: "deprecated_fields",
        },
        {
            label: "Show send email button?",
            name: "show_send_email_button",
            description: "[Deprecated] Show send email button on the pdf report. Use teams_that_can_send instead.",
            ...yesOrNoRadio
        },
    ];

    return (
        <div className="d-flex flex-column gap-5">
            <SanitizeUnexpectedFields form={form} object={config_data} setObject={setConfigData} />

            <MetaForm
                withSearch
                groupFields
                meta={form}
                setValue={(field, value) => setConfigData({ ...config_data, [field]: value })}
                object={config_data}
                config={config}
            />
        </div>
    );
}
